import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { PickerItem } from './pickerItems';
import { pickerMessages } from './pickerMessages';
import { SMLPickerPicture } from './SMLPickerPicture';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPickerCardProps {
  item: PickerItem;
  theme: SerializedSmartLinkVersionTheme;
  /** The search words, marked in the line. */
  query: string;
  onPick: (item: PickerItem) => void;
}

/** Marks the first match of the search in a line (design b2). */
function markMatch(line: string, query: string): React.ReactNode {
  const needle = query.trim().toLowerCase();
  const at = needle === '' ? -1 : line.toLowerCase().indexOf(needle);
  if (at === -1) {
    return line;
  }
  return (
    <>
      {line.slice(0, at)}
      <mark className={styles.match}>{line.slice(at, at + needle.length)}</mark>
      {line.slice(at + needle.length)}
    </>
  );
}

/** One block of the picker (design b1–b4): the picture, the name, the benefit line, the chips. */
const SMLPickerCardComponent: React.FC<SMLPickerCardProps> = ({ item, theme, query, onPick }) => {
  const intl = useIntl();
  const pick = useCallback(() => onPick(item), [item, onPick]);
  return (
    <button
      type="button"
      className={clsx(styles.card, item.onPageUUID !== null && styles.cardDim, item.locked && styles.cardLocked)}
      onClick={pick}
      data-testid={`picker-card-${item.type}`}
    >
      <SMLPickerPicture type={item.type} theme={theme} />
      <span className={styles.cardBody}>
        <span className={styles.cardName}>
          {item.name}
          {item.locked && <LockChip feature={SMLUpgradeFeature.presave} />}
          {item.onPageUUID !== null && (
            <span className={styles.onPage}>{intl.formatMessage(pickerMessages.onThisPage)}</span>
          )}
        </span>
        <span className={styles.cardLine}>{markMatch(item.line, query)}</span>
      </span>
    </button>
  );
};

export const SMLPickerCard = TypedMemo(SMLPickerCardComponent);
